/**
 * Escapes HTML characters to prevent XSS injection.
 * @param {string} str
 * @returns {string}
 */
export function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Safely converts Markdown formatting (bold, italics, line breaks) to HTML
 * while strictly escaping raw HTML input to neutralize Cross-Site Scripting (XSS).
 * @param {string} markdown
 * @returns {string}
 */
export function sanitizeMarkdown(markdown) {
  if (!markdown || typeof markdown !== 'string') return '';

  // 1. Strictly escape all HTML special characters first
  const sanitized = escapeHtml(markdown);

  // 2. Safely apply Markdown formatting on escaped text
  return sanitized
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>');
}

/**
 * Clears cached user profile information from storage upon logout or session invalidation.
 * Notice: JWT tokens and UIDs are strictly forbidden from client storage.
 */
export function clearAuthSession() {
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('token');
  localStorage.removeItem('uid');
  localStorage.removeItem('name');
  localStorage.removeItem('age');
  localStorage.removeItem('email');
  localStorage.removeItem('gender');
  localStorage.removeItem('height');
  localStorage.removeItem('weight');
  localStorage.removeItem('neck');
  localStorage.removeItem('waist');
}

/**
 * Initiates server-side logout to clear HttpOnly authentication cookies
 * and purges local client profile state only if the server successfully invalidates the session.
 * Throws an error on server/network failure without falsely representing the user as logged out.
 */
export async function logoutSession() {
  const response = await fetch('https://thefitrealm.in/api/logout', {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Server logout failed with status: ${response.status}`);
  }

  clearAuthSession();
  return { success: true };
}

/**
 * Robust wrapper for fetch that enforces credential inclusion,
 * intercepts 401/403 session expirations without leaking sensitive data,
 * and handles JSON parsing safely.
 *
 * @param {string} url
 * @param {RequestInit} options
 * @returns {Promise<Response>}
 */
export async function apiFetch(url, options = {}) {
  const fetchOptions = {
    ...options,
    credentials: 'include',
  };

  const response = await fetch(url, fetchOptions);

  if (response.status === 401 || response.status === 403) {
    clearAuthSession();
  }

  return response;
}
